import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { useUser } from '../../contexts/UserContext';
import { useCounters } from '../../contexts/CounterContext';
import { deleteClickEvent } from '../../services/api';
import { ClickEvent, CustomButton } from '../../types';
import { useToast } from '../../utils/toastUtils';
import { createDateFormatters } from '../../utils/dateUtils';
import ToastContainer from '../common/ToastContainer';
import LoadingSpinner from '../common/LoadingSpinner';
import { useOwnerUrl } from '../../hooks/useOwnerUrl';
import { config } from '../../config';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useHistory as useHistoryHook } from '../../hooks/useHistory';
import { isOwnerUrlIdentifier } from '../../utils/urlUtils';
import { useTooltips } from '../../utils/tooltipUtils';
import Pagination from '../common/Pagination';

function History() {
  const { t, i18n } = useTranslation();
  const { isAdmin, hasCounterAccess, user } = useUser();
  const { toasts, showToast } = useToast();
  const { counterId } = useParams<{ counterId?: string }>();
  const { selectedCounterId, selectCounter, findCounterByOwnerUrl, counters, loading: countersLoading } = useCounters();
  const navigate = useNavigate();
  const [counterNotFound, setCounterNotFound] = useState(false);
  
  // Use the new owner URL hook
  const { isOwnerUrl, counterIdentifier } = useOwnerUrl(counterId);
  
  // Define state variables we still need
  const [selectedButton, setSelectedButton] = useState<string>('all');
  const [eventToDelete, setEventToDelete] = useState<ClickEvent | null>(null);
  
  // Modal reference
  const modalRef = useRef<HTMLDivElement>(null);
  const modalInstance = useRef<Modal | null>(null);

  // Create date formatter based on browser locale
  const { formatDate } = createDateFormatters(i18n.language);
  
  // Get the counter name to use in the document title
  const currentCounter = counterIdentifier ? 
    counters.find(c => isOwnerUrl ? c.ownerUrl === counterIdentifier : c._id === counterIdentifier) : 
    undefined;
  
  // Set document title with counter name and meta description for better sharing
  useDocumentTitle(
    currentCounter?.name,
    'TapDonkey',
    currentCounter?.name ? `${currentCounter.name} - Track and analyze clicks with TapDonkey` : undefined
  );

  // Use our new history hook that uses the useManualData hook internally
  const { 
    clickEvents, 
    buttons, 
    loading, 
    refreshing, 
    error, 
    refetchHistory, 
    refetchButtons,
    paginatedEvents,
    totalPages,
    itemsPerPage,
    handleItemsPerPageChange,
    currentPage,
    setCurrentPage
  } = useHistoryHook({
    counterIdentifier: counterIdentifier || '',
    isOwnerUrl,
    selectedButton
  });

  // Use our new tooltip utility for safe tooltip management
  useTooltips([loading, refreshing, currentPage, selectedButton]);
  
  // Initialize modal when component mounts
  useEffect(() => {
    // Wait for the DOM to be ready
    if (modalRef.current) {
      // Create a new Modal instance
      modalInstance.current = new Modal(modalRef.current, {
        backdrop: 'static',
        keyboard: false
      });
    }
    
    // Cleanup function to dispose of modal when component unmounts
    return () => {
      if (modalInstance.current) {
        modalInstance.current.dispose();
      }
    };
  }, []);

  // Check if the counter exists when data is loaded
  useEffect(() => {
    // If we have an error or if data is loaded but no counter in the list
    if ((!loading && error) || 
        (!loading && !currentCounter && counterIdentifier)) {
      setCounterNotFound(true);
    }
  }, [loading, error, currentCounter, counterIdentifier]);

  // If no counterId is found, redirect to counters list
  if (!counterId) {
    return <Navigate to="/counters" replace />;
  }
  
  // Don't make any redirect decisions while counters are still loading
  if (countersLoading) {
    return <LoadingSpinner size="lg" />;
  }
  
  // Determine if this is an owner URL
  const isOwnerUrlAccess = isOwnerUrlIdentifier(counterId, counters);
  
  // Get the counter from context
  const counter = counters.find(c => c._id === counterId || c.ownerUrl === counterId);
  
  // If counter not found, redirect to not found page
  if (!counter) {
    return <Navigate to="/counter-not-found" replace />;
  }
  
  // Check access using the hasCounterAccess helper
  const hasViewAccess = hasCounterAccess(counter.ownerEmail || null, isOwnerUrlAccess, 'view');
  const hasManageAccess = hasCounterAccess(counter.ownerEmail || null, isOwnerUrlAccess, 'manage');
  
  // Redirect if no view access
  if (!hasViewAccess) {
    // For owner URLs of logged-in user counters, show "not found" page if denied access for public users
    if (isOwnerUrlAccess && counter.ownerEmail !== null && user.email === null) {
      return <Navigate to="/counter-not-found" replace />;
    }
    
    // For other denied access cases, redirect to counters list
    return <Navigate to="/counters" replace />;
  }

  if (loading && clickEvents.length === 0) {
    return <LoadingSpinner />;
  }

  const handleDelete = (event: ClickEvent) => {
    setEventToDelete(event);
    
    // Use vanilla JS to show the modal as a fallback
    const modalElement = document.getElementById('deleteModal');
    if (modalElement) {
      const bsModal = new Modal(modalElement);
      bsModal.show();
    } else {
      console.error("Modal element not found");
    }
  };

  const confirmDelete = async () => {
    if (!eventToDelete || !counterIdentifier) return;

    try {
      await deleteClickEvent(counterIdentifier, eventToDelete._id, isOwnerUrl);
      
      // Refresh data after deletion
      refetchHistory();
      showToast(t('history.deleteSuccess', 'Click entry deleted successfully'));
    } catch (err) {
      console.error('Error deleting click event:', err);
      showToast(t('history.deleteFailed', 'Failed to delete click entry'), 'danger');
    } finally {
      setEventToDelete(null);
      
      // Hide modal using vanilla JS as fallback
      const modalElement = document.getElementById('deleteModal');
      if (modalElement) {
        const bsModal = Modal.getInstance(modalElement);
        if (bsModal) {
          bsModal.hide();
        }
      }
    }
  };

  // Add a manual refresh function for the refresh button
  const handleManualRefresh = () => {
    refetchHistory();
    refetchButtons();
  };

  return (
    <div className="container py-3 py-md-5">
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="text-center mb-3 mb-md-4">
            <h1 className="h3 h1-md fw-bold mb-0">{t('history.title')}</h1>
          </div>

          {/* Refresh button for manual updates */}
          <div className="refresh-container mb-3">
            <button 
              className="btn btn-outline-primary d-flex align-items-center" 
              onClick={handleManualRefresh}
              disabled={loading || refreshing}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={t('history.tooltips.refresh')}
            >
              {refreshing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <span>{t('common.refreshing')}</span>
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  <span>{t('common.refresh')}</span>
                </>
              )}
            </button>
            <small className="text-muted ms-3 d-none d-md-inline">
              {t('history.manualRefresh')}
            </small>
          </div>

          {/* Button Filter */}
          <div className="mb-3">
            <select 
              className="form-select"
              value={selectedButton}
              onChange={(e) => setSelectedButton(e.target.value)}
              disabled={loading}
              aria-label={t('history.filterByButton')}
            >
              <option value="all">{t('history.allButtons')}</option>
              {buttons.map(button => (
                <option key={button._id} value={button._id}>{button.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {refreshing && (
            <div className="loading-indicator">
              <div className="spinner-border spinner-border-sm text-secondary spinner-sm" role="status">
                <span className="visually-hidden">{t('common.refreshing')}</span>
              </div>
            </div>
          )}

          {clickEvents.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-clock-history display-1 text-muted mb-3"></i>
              <p className="text-muted">{t('history.noHistory')}</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover align-middle table-bordered table-sm caption-top mb-0">
                  <caption className="small text-body-secondary text-center border-top pt-2 ps-3 pe-3">
                    {t('history.clickActivityCaption')}
                  </caption>
                  <thead className="table-light small">
                    <tr>
                      <th className="d-table-cell">{t('history.timestamp')}</th>
                      <th className="d-table-cell">{t('history.button')}</th>
                      {hasManageAccess && <th className="d-none d-md-table-cell">{t('history.browser')}</th>}
                      {hasManageAccess && <th className="d-none d-md-table-cell">{t('history.os')}</th>}
                      {hasManageAccess && <th className="d-none d-md-table-cell">{t('history.ip')}</th>}
                      {hasManageAccess && <th className="text-end d-table-cell">{t('common.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEvents.map(event => (
                      <tr key={event._id}>
                        <td className="small d-table-cell">{formatDate(event.timestamp)}</td>
                        <td className="d-table-cell">
                          <div className="d-flex align-items-center button-name" style={{ '--button-color': event.buttonColor } as React.CSSProperties}>
                            {event.buttonIcon && <i className={`${event.buttonIcon} me-1 icon`}></i>}
                            <span className="fw-medium small text-truncate">{event.buttonName}</span>
                          </div>
                          {hasManageAccess && (
                            <div className="mt-1 small text-muted d-md-none">
                              <div>{event.deviceInfo.browser || t('common.unknown')} / {event.deviceInfo.os || t('common.unknown')}</div>
                              <div>{event.deviceInfo.ip || t('common.unknown')}</div>
                            </div>
                          )}
                        </td>
                        {hasManageAccess && <td className="small d-none d-md-table-cell">{event.deviceInfo.browser || t('common.unknown')}</td>}
                        {hasManageAccess && <td className="small d-none d-md-table-cell">{event.deviceInfo.os || t('common.unknown')}</td>}
                        {hasManageAccess && <td className="small d-none d-md-table-cell">{event.deviceInfo.ip || t('common.unknown')}</td>}
                        {hasManageAccess && (
                          <td className="text-end d-table-cell">
                            <button 
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(event)}
                              title={t('history.tooltips.delete')}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination using our reusable component */}
              {clickEvents.length > 0 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  paginate={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={clickEvents.length}
                  itemsPerPageOptions={config.pagination.itemsPerPageOptions}
                  handleItemsPerPageChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                  ariaLabel={t('pagination.historyPagination', 'Click history pagination')}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal - Render for admins or when accessed via owner URL */}
      <div 
        className="modal fade" 
        id="deleteModal" 
        ref={modalRef}
        tabIndex={-1} 
        aria-labelledby="deleteModalLabel" 
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteModalLabel">{t('history.confirmDeleteClick')}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
            </div>
            <div className="modal-body">
              {t('history.confirmDeleteClickMessage')}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                {t('common.deleteTap')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} title={t('history.title')} />
    </div>
  );
}

export default History; 